// src/App.js - Korrigierte Version mit richtigen API-Endpunkten

import React, { useState, useEffect } from 'react';
import './App.css';
import { 
  Check, Menu, X, ChevronRight, User, LogOut, 
  Trash2, Edit2, Save, XCircle, Settings, 
  Bold, Italic, List, ListOrdered 
} from 'lucide-react';

// API Konfiguration - WICHTIG: Anpassen an Ihre Domain!
const API_URL = window.location.hostname === 'localhost' 
  ? 'https://localhost/hundetraining/api'  // Für lokales Testen - Pfad anpassen!
  : '/api';  // Für Produktion (relative URL)

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentLesson, setCurrentLesson] = useState(1);
  const [completedLessons, setCompletedLessons] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [lessonNotes, setLessonNotes] = useState({});
  const [loading, setLoading] = useState(true);

  // Check for saved token on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    
    if (token && user) {
      const userData = JSON.parse(user);
      setCurrentUser(userData);
      setIsLoggedIn(true);
      setIsAdmin(userData.role === 'admin');
      loadLessons();
    } else {
      setLoading(false);
    }
  }, []);

  // API Funktionen
  const apiCall = async (endpoint, options = {}) => {
    const token = localStorage.getItem('token');
    
    const config = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
        ...(token && { Authorization: `Bearer ${token}` })
      }
    };
    
    try {
      const response = await fetch(`${API_URL}${endpoint}`, config);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'API Error');
      }
      
      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  };

  // Load lessons from API
  const loadLessons = async () => {
    try {
      setLoading(true);
      const response = await apiCall('/lessons.php');  // .php hinzugefügt!
      if (response.success) {
        setLessons(response.lessons);
        // Collect completed lessons
        const completed = response.lessons
          .filter(l => l.completed == 1)  // == statt === weil MySQL "1" als String zurückgibt
          .map(l => parseInt(l.id));
        setCompletedLessons(completed);
      }
    } catch (error) {
      console.error('Error loading lessons:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle login - KORRIGIERT!
  const handleLogin = async (email, password) => {
    try {
      const response = await apiCall('/auth.php?action=login', {  // Korrigierte URL!
        method: 'POST',
        body: JSON.stringify({ email, password })
      });
      
      if (response.success) {
        localStorage.setItem('token', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        setCurrentUser(response.user);
        setIsLoggedIn(true);
        setIsAdmin(response.user.role === 'admin');
        setShowAuthModal(false);
        await loadLessons();
      }
    } catch (error) {
      alert('Login fehlgeschlagen: ' + error.message);
    }
  };

  // Handle registration - KORRIGIERT!
  const handleRegister = async (email, password, name) => {
    try {
      const response = await apiCall('/auth.php?action=register', {  // Korrigierte URL!
        method: 'POST',
        body: JSON.stringify({ email, password, name })
      });
      
      if (response.success) {
        localStorage.setItem('token', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        setCurrentUser(response.user);
        setIsLoggedIn(true);
        setShowAuthModal(false);
        await loadLessons();
      }
    } catch (error) {
      alert('Registrierung fehlgeschlagen: ' + error.message);
    }
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsLoggedIn(false);
    setCurrentUser(null);
    setIsAdmin(false);
    setLessons([]);
    setCompletedLessons([]);
  };

  // Toggle lesson completion - KORRIGIERT!
  const markLessonComplete = async (lessonId) => {
    try {
      const response = await apiCall('/lessons.php', {  // .php hinzugefügt!
        method: 'POST',
        body: JSON.stringify({ 
          toggle_progress: true,
          lesson_id: lessonId 
        })
      });
      
      if (response.success) {
        if (response.completed) {
          setCompletedLessons([...completedLessons, lessonId]);
        } else {
          setCompletedLessons(completedLessons.filter(id => id !== lessonId));
        }
      }
    } catch (error) {
      alert('Fehler beim Aktualisieren des Fortschritts');
    }
  };

  // Load notes for a lesson - KORRIGIERT!
  const loadNotes = async (lessonId) => {
    try {
      const response = await apiCall(`/notes.php?lesson_id=${lessonId}`);  // .php hinzugefügt!
      if (response.success) {
        setLessonNotes({
          ...lessonNotes,
          [lessonId]: response.notes
        });
      }
    } catch (error) {
      console.error('Error loading notes:', error);
    }
  };

  // Create note - KORRIGIERT!
  const addNote = async (lessonId, noteText) => {
    if (!noteText.trim()) return;
    
    try {
      const response = await apiCall('/notes.php', {  // .php hinzugefügt!
        method: 'POST',
        body: JSON.stringify({ 
          lesson_id: lessonId,
          note_text: noteText 
        })
      });
      
      if (response.success) {
        const currentNotes = lessonNotes[lessonId] || [];
        setLessonNotes({
          ...lessonNotes,
          [lessonId]: [...currentNotes, response.note]
        });
      }
    } catch (error) {
      alert('Fehler beim Speichern der Notiz');
    }
  };

  // Auth Modal Component
  const AuthModal = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    
    const handleSubmit = async () => {
      setErrorMessage('');
      
      // Validierung
      if (!email || !password) {
        setErrorMessage('Bitte alle Felder ausfüllen');
        return;
      }
      
      if (authMode === 'register' && !name) {
        setErrorMessage('Bitte Namen eingeben');
        return;
      }
      
      try {
        if (authMode === 'login') {
          await handleLogin(email, password);
        } else {
          await handleRegister(email, password, name);
        }
      } catch (error) {
        setErrorMessage(error.message);
      }
    };
    
    return (
      <div className="modal-overlay">
        <div className="modal">
          <div className="modal-header">
            <h2>{authMode === 'login' ? 'Anmelden' : 'Registrieren'}</h2>
            <button onClick={() => setShowAuthModal(false)}>
              <X />
            </button>
          </div>
          
          <div className="modal-body">
            {authMode === 'register' && (
              <input
                type="text"
                placeholder="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input"
                onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
              />
            )}
            <input
              type="email"
              placeholder="E-Mail"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
            />
            <input
              type="password"
              placeholder="Passwort"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
            />
            
            {errorMessage && (
              <div style={{color: 'red', marginBottom: '1rem', textAlign: 'center'}}>
                {errorMessage}
              </div>
            )}
            
            <button
              onClick={handleSubmit}
              className="btn btn-primary"
            >
              {authMode === 'login' ? 'Anmelden' : 'Registrieren'}
            </button>
            
            <p className="text-center">
              {authMode === 'login' ? (
                <>
                  Noch kein Konto?{' '}
                  <button
                    onClick={() => {
                      setAuthMode('register');
                      setErrorMessage('');
                    }}
                    className="link"
                  >
                    Registrieren
                  </button>
                </>
              ) : (
                <>
                  Bereits registriert?{' '}
                  <button
                    onClick={() => {
                      setAuthMode('login');
                      setErrorMessage('');
                    }}
                    className="link"
                  >
                    Anmelden
                  </button>
                </>
              )}
            </p>
            
            {authMode === 'login' && (
              <p className="text-center" style={{marginTop: '1rem', fontSize: '0.9rem', color: '#666'}}>
                Demo: admin@ihre-domain.de / admin123
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Main App Render
  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Lädt...</p>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="login-page">
        <div className="login-card">
          <h1>🐕 Hundetraining</h1>
          <p>Professionelles Training für Ihren Vierbeiner</p>
          <button
            onClick={() => setShowAuthModal(true)}
            className="btn btn-primary"
          >
            Anmelden / Registrieren
          </button>
          
          <div style={{marginTop: '2rem', fontSize: '0.8rem', color: '#666'}}>
            <p>Zum Testen:</p>
            <p>admin@ihre-domain.de / admin123</p>
          </div>
        </div>
        {showAuthModal && <AuthModal />}
      </div>
    );
  }

  const currentLessonData = lessons.find(l => l.id == currentLesson) || lessons[0];
  const completionRate = lessons.length > 0 
    ? Math.round((completedLessons.length / lessons.length) * 100) 
    : 0;

  return (
    <div className="app">
      {/* Mobile Header */}
      <header className="mobile-header">
        <button onClick={() => setSidebarOpen(true)}>
          <Menu />
        </button>
        <h1>Hundetraining</h1>
        <button onClick={handleLogout}>
          <LogOut />
        </button>
      </header>

      {/* Sidebar */}
      <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <button onClick={() => setSidebarOpen(false)} className="close-btn">
            <X />
          </button>
		  <img src="/logo.png" class="bb-logo" />
        </div>
        
        <div className="progress-bar">
          <div className="progress-info">
            <span>Fortschritt</span>
            <span>{completionRate}%</span>
          </div>
          <div className="progress-track">
            <div 
              className="progress-fill" 
              style={{ width: `${completionRate}%` }}
            />
          </div>
          <p className="progress-text">
            {completedLessons.length} von {lessons.length} Lektionen
          </p>
        </div>

        {isAdmin && (
          <button 
            onClick={() => alert('Admin Panel in Entwicklung')}
            className="btn btn-admin"
          >
            <Settings /> Admin Panel
          </button>
        )}

        <div className="lesson-list">
          {lessons.map((lesson) => (
            <button
              key={lesson.id}
              onClick={() => {
                setCurrentLesson(lesson.id);
                setSidebarOpen(false);
                loadNotes(lesson.id);
              }}
              className={`lesson-item ${currentLesson == lesson.id ? 'active' : ''}`}
            >
              <span>{lesson.title}</span>
              {completedLessons.includes(parseInt(lesson.id)) && (
                <Check className="check-icon" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <main className="main-content">
        {currentLessonData ? (
          <div className="lesson-content">
            <h1>{currentLessonData.title}</h1>
            {completedLessons.includes(parseInt(currentLessonData.id)) && (
              <span className="completed-badge">
                <Check /> Abgeschlossen
              </span>
            )}

            <div className="content-card">
              <h2>Theorie</h2>
              <div dangerouslySetInnerHTML={{ __html: currentLessonData.theory }} />
            </div>

            <div className="content-card exercise">
              <h2>{currentLessonData.exercise_title}</h2>
              <div dangerouslySetInnerHTML={{ __html: currentLessonData.exercise_description }} />
            </div>

            <div className="action-buttons">
              <button
                onClick={() => markLessonComplete(parseInt(currentLessonData.id))}
                className={`btn ${completedLessons.includes(parseInt(currentLessonData.id)) ? 'btn-secondary' : 'btn-success'}`}
              >
                {completedLessons.includes(parseInt(currentLessonData.id)) 
                  ? 'Als unerledigt markieren' 
                  : 'Als erledigt markieren'}
              </button>
              
              {parseInt(currentLessonData.id) < lessons.length && (
                <button
                  onClick={() => {
                    const nextLesson = lessons.find(l => parseInt(l.id) > parseInt(currentLessonData.id));
                    if (nextLesson) {
                      setCurrentLesson(nextLesson.id);
                    }
                  }}
                  className="btn btn-primary"
                  style={{marginLeft: '1rem'}}
                >
                  Nächste Lektion <ChevronRight style={{display: 'inline', marginLeft: '0.5rem'}} />
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="lesson-content">
            <p>Keine Lektionen verfügbar. Bitte laden Sie die Seite neu oder kontaktieren Sie den Administrator.</p>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;